import { Schema } from "effect"
import { AgentKind } from "./common"
import { AgentId, Millis, ProfileId, RepoId, RunId, RunnerId, TaskId, UserId } from "./ids"

// #region placement
/**
 * Tool capability token: lowercase name, optional "@version". A requirement "xcode@26" is met by "xcode@26" or
 * "xcode@26.1"; a requirement "xcode" is met by any "xcode@*". Examples: simulator, docker, fastlane, computer-use.
 */
export const ToolCap = Schema.String.check(Schema.isPattern(/^[a-z][a-z0-9-]*(@[0-9][0-9a-z.]*)?$/))
export const Os = Schema.Literals(["macos", "linux", "windows"])
export const Trust = Schema.Literals(["local", "cloud", "hosted"])
/** Free-form "key=value" labels, e.g. net=home. */
export const Labels = Schema.Record(Schema.String, Schema.String)

/** Hard requirements. All must hold. Lists of things a run needs are "all of"; lists of alternatives are "any of". */
export const Requires = Schema.Struct({
  agent: Schema.optionalKey(Schema.NonEmptyArray(AgentKind)), // any of
  tools: Schema.optionalKey(Schema.Array(ToolCap)), // all of
  repos: Schema.optionalKey(Schema.Array(RepoId)), // all of
  os: Schema.optionalKey(Schema.NonEmptyArray(Os)), // any of
  trust: Schema.optionalKey(Schema.NonEmptyArray(Trust)), // any of
  labels: Schema.optionalKey(Labels), // all of
  /** Pins. `mind: runner:vps-1` compiles to { runner }. A pinned run waits for that runner; it never falls back. */
  runner: Schema.optionalKey(RunnerId),
  profile: Schema.optionalKey(ProfileId),
})
export type Requires = typeof Requires.Type

/** Soft preference: the scheduler adds `weight` when `match` holds. */
export const Prefer = Schema.Struct({ match: Requires, weight: Schema.Number })

export const Affinity = Schema.Struct({
  /** Reuse the worktree/branch another task created (T-412 builds on T-388's branch). */
  worktreeOf: Schema.optionalKey(TaskId),
  /** Keep native-session locality: prefer the runner+profile that ran this agent's last session. */
  sessionOf: Schema.optionalKey(AgentId),
  /** soft adds score; hard turns it into a requirement and the run waits. */
  strength: Schema.Literals(["soft", "hard"]),
})

/**
 * Named mutexes taken atomically with the lease and released with it. "ws:" keys are workspace-wide
 * ("ws:repo:dictly:release"); "runner:" keys are per runner ("runner:computer-use": one GUI session per Mac).
 */
export const ExclusiveKey = Schema.String.check(Schema.isPattern(/^(ws|runner):[a-z0-9:_.-]+$/))

export const Placement = Schema.Struct({
  requires: Requires,
  prefers: Schema.Array(Prefer),
  affinity: Schema.NullOr(Affinity),
  exclusive: Schema.Array(ExclusiveKey),
})
export type Placement = typeof Placement.Type

/** Area defaults: placement without affinity (affinity is always about a specific task or agent). */
export const PlacementDefaults = Schema.Struct({
  requires: Requires,
  prefers: Schema.Array(Prefer),
  exclusive: Schema.Array(ExclusiveKey),
})

/** Why a run is waiting. Shown verbatim on the canvas ("waiting for runner: needs xcode@26; jannik-mbp is asleep"). */
export const Unmet = Schema.TaggedUnion({
  no_runner_online: {},
  runner_offline: { runnerId: RunnerId, lastSeenAt: Schema.NullOr(Millis) },
  runner_paused: { runnerId: RunnerId },
  owner_lock: { principal: UserId },
  ceiling_refuses: { runnerId: RunnerId, why: Schema.String },
  missing_tool: { tool: ToolCap },
  missing_repo: { repoId: RepoId },
  missing_agent: { agent: Schema.Array(AgentKind) },
  profile_not_ready: { runnerId: RunnerId, profileId: ProfileId, why: Schema.String },
  profile_out_of_scope: { runnerId: RunnerId, profileId: ProfileId },
  profile_at_capacity: { runnerId: RunnerId, profileId: ProfileId },
  rate_limited: { runnerId: RunnerId, profileId: ProfileId, resetsAt: Schema.NullOr(Millis) },
  at_capacity: { runnerId: RunnerId },
  exclusive_held: { key: ExclusiveKey, heldBy: RunId },
  affinity_unmet: { worktreeOf: Schema.NullOr(TaskId) },
  /** The owner's runner is connected only on the legacy JSON socket (below protocol 1): shown as outdated (B11). */
  runner_outdated: { runnerId: RunnerId, version: Schema.String, minimum: Schema.String },
})
export type Unmet = typeof Unmet.Type

export const LeaseTiming = {
  LEASE_TTL_MS: 60_000,
  RENEW_EVERY_MS: 20_000,
  HEARTBEAT_EVERY_MS: 10_000,
  OFFER_TTL_MS: 10_000,
  MCP_TOKEN_GRACE_MS: 10_000,
  SELF_FENCE_MARGIN_MS: 5_000,
  INTERRUPT_FORCE_CLOSE_MS: 10_000,
  MAX_PRESTART_RELEASES: 3,
} as const
// #endregion placement
